import React, { memo } from 'react'
import {
  Box,
  HStack,
  VStack,
  Text,
  IconButton,
  Checkbox,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Tooltip,
  useMediaQuery
} from '@chakra-ui/react'
import SvgIcon from 'components/SvgIcon'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { S3FileTypeEnum } from 'constants/aws'
import { getS3FileUrl } from 'utils/images'
import { IProcessWithRelations } from 'interfaces/process'
import { ICollection } from 'interfaces/collection'
import styles from '../searchPage.module.scss'
import colors from 'theme/colors.theme'

interface SearchResultCardProps {
  item: IEsGlobalSearchItem | IProcessWithRelations
  title: React.ReactNode
  subtitle?: React.ReactNode
  leadingIcon: React.ReactNode
  isManageMode: boolean
  canManageActions: boolean
  isSelected: boolean
  isEditor: boolean
  onToggleSelect: () => void
  onClick: () => void
  onQuickView: () => void
  onShare?: () => void
  onArchive?: () => void
  onMakeCopy?: () => void
  onRemove?: () => void
  canShowRemove?: boolean
}

const SearchResultCard = ({
  item,
  title,
  subtitle,
  leadingIcon,
  isManageMode,
  canManageActions,
  isSelected,
  isEditor,
  onToggleSelect,
  onClick,
  onQuickView,
  onShare,
  onArchive,
  onMakeCopy,
  onRemove,
  canShowRemove
}: SearchResultCardProps) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const isEsGlobalSearchItem = (item: IEsGlobalSearchItem | IProcessWithRelations): item is IEsGlobalSearchItem => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'collectionName' in item &&
      'correlatingId' in item &&
      'correlatingData' in item
    )
  }

  const isSearchItem = isEsGlobalSearchItem(item)
  const searchItem = isSearchItem ? item : null
  const processItem = isSearchItem ? null : item

  const isProcess: boolean = isSearchItem ? searchItem?.collectionName === EEsGlobalSearchCollection.PROCESS : true

  const organizationId: number | undefined = isSearchItem
    ? (searchItem?.correlatingData as IProcessWithRelations | ICollection)?.organizationId
    : processItem?.organizationId

  const mainMediaKey: string | undefined = isSearchItem
    ? isProcess
      ? (searchItem?.correlatingData as IProcessWithRelations)?.mainMedia
      : (searchItem?.correlatingData as ICollection)?.mainMedia
    : processItem?.mainMedia

  const mainMediaUrl: string | undefined =
    mainMediaKey && organizationId ? getS3FileUrl(S3FileTypeEnum.IMAGE, organizationId, mainMediaKey) : undefined

  const renderActions = () => {
    if (isMobile) {
      return (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="More"
            size="sm"
            className={styles.menuIcon}
            icon={<SvgIcon size={16} iconName="show-more-icon" />}
          />
          <MenuList className={styles.menuList}>
            <MenuItem onClick={onQuickView} icon={<SvgIcon size={15} iconName="ic_detail" />}>
              Quick view
            </MenuItem>
            {canManageActions && (searchItem as { isPublished?: boolean })?.isPublished && onShare && (
              <MenuItem onClick={onShare} icon={<SvgIcon size={15} iconName="ic_share" />}>
                Share
              </MenuItem>
            )}
            {canManageActions && onMakeCopy && isEditor && isProcess && (
              <MenuItem onClick={onMakeCopy} icon={<SvgIcon size={15} iconName="copy" />}>
                Make a copy
              </MenuItem>
            )}
            {canShowRemove && onRemove && (
              <MenuItem onClick={onRemove} icon={<SvgIcon size={15} iconName="material-symbols_link-off" />}>
                Remove
              </MenuItem>
            )}
            {canManageActions && onArchive && (
              <MenuItem onClick={onArchive} icon={<SvgIcon size={15} iconName="archive" />}>
                Archive
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      )
    }

    return (
      <HStack spacing={2} className={styles.actionsContainer}>
        <Tooltip
          label="Quick view"
          height="36px"
          fontSize="14px"
          padding={2}
          background="gray.700"
          placement="top"
          color="white"
          hasArrow
          borderRadius="4px"
        >
          <IconButton
            variant="transparent"
            colorScheme="#F7FAFC"
            aria-label="Quick view"
            className={styles.iconButton}
            size="xs"
            icon={<SvgIcon iconName="ic_detail" className={styles.iconShare} />}
            onClick={onQuickView}
          />
        </Tooltip>
        {isManageMode && canManageActions && (searchItem as { isPublished?: boolean })?.isPublished && onShare && (
          <Tooltip
            label="Share"
            height="36px"
            fontSize="14px"
            padding={2}
            background="gray.700"
            placement="top"
            color="white"
            hasArrow
            borderRadius="4px"
          >
            <IconButton
              variant="transparent"
              colorScheme="#F7FAFC"
              aria-label="Share"
              className={styles.iconButton}
              size="xs"
              icon={<SvgIcon iconName="ic_share" className={styles.iconShare} />}
              onClick={onShare}
            />
          </Tooltip>
        )}
        {canShowRemove && onRemove && (
          <Tooltip
            label="Remove from collection"
            height="36px"
            fontSize="14px"
            padding={2}
            background="gray.700"
            placement="top"
            color="white"
            hasArrow
            borderRadius="4px"
          >
            <IconButton
              variant="transparent"
              colorScheme="#F7FAFC"
              aria-label="Remove"
              className={styles.iconButton}
              size="xs"
              icon={<SvgIcon iconName="material-symbols_link-off" className={styles.iconShare} />}
              onClick={onRemove}
            />
          </Tooltip>
        )}
        {isManageMode && canManageActions && ((onMakeCopy && isEditor && isProcess) || onArchive) && (
          <Menu>
            <MenuButton
              as={IconButton}
              className={styles.menuIcon}
              aria-label="Options"
              size="xs"
              icon={<SvgIcon size={16} iconName="show-more-icon" />}
            />
            <MenuList>
              {onMakeCopy && isEditor && isProcess && (
                <MenuItem
                  background="white"
                  border="none"
                  color="gray.700"
                  icon={<SvgIcon size={15} iconName="copy" color={colors.gray[700]} />}
                  onClick={onMakeCopy}
                >
                  Make a copy
                </MenuItem>
              )}
              {onArchive && (
                <MenuItem
                  background="white"
                  border="none"
                  color="gray.700"
                  icon={<SvgIcon size={15} iconName="archive" color={colors.gray[700]} />}
                  onClick={onArchive}
                >
                  Archive {isProcess ? 'process' : 'collection'}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        )}
      </HStack>
    )
  }

  return (
    <VStack
      width="full"
      align="stretch"
      spacing={2}
      borderBottom="1px solid"
      backgroundColor="white"
      borderColor="gray.200"
      borderRadius="8px"
      paddingY={isMobile ? 2 : 4}
      paddingX={isMobile ? 3 : 4}
    >
      <HStack align="flex-start" spacing={4} width="full">
        <>
          {isManageMode && <Checkbox isChecked={isSelected} onChange={() => onToggleSelect()} />}
          {mainMediaUrl ? (
            <Box
              width="80px"
              height="80px"
              borderRadius="8px"
              overflow="hidden"
              flexShrink={0}
              onClick={onClick}
              cursor="pointer"
            >
              <Image src={mainMediaUrl} alt="preview" width="100%" height="100%" objectFit="cover" />
            </Box>
          ) : null}
          <VStack flex={1} minW={0} align="stretch" spacing={2}>
            <HStack align="center" spacing={3} width="full">
              <Box
                width="24px"
                height="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="8px"
                overflow="hidden"
                flexShrink={0}
                sx={{ '& svg': { width: '24px', height: '24px' } }}
              >
                {leadingIcon}
              </Box>
              <Box flex={1} minW={0} onClick={onClick} cursor="pointer">
                <Text
                  noOfLines={2}
                  fontWeight={500}
                  fontSize="16px"
                  color="gray.700"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  minW={0}
                  maxW="100%"
                  display="block"
                >
                  {title}
                </Text>
              </Box>
              {!isMobile && (
                <HStack spacing={2} justifyContent="flex-end" flexShrink={0}>
                  {!isEditor && (
                    <Tag
                      variant="solid"
                      background="gray.100"
                      border="1px solid"
                      borderColor="gray.300"
                      width="90px"
                      height="24px"
                      color="gray.500"
                      borderRadius="6px"
                      fontSize="14px"
                      paddingY={0.5}
                      paddingX={2}
                      alignItems="center"
                    >
                      View Only
                    </Tag>
                  )}
                  <Tag
                    variant="solid"
                    background={isProcess ? '#00A9EB' : '#4CA817'}
                    width="auto"
                    height="24px"
                    color="white"
                    borderRadius="6px"
                    fontSize="14px"
                    paddingY={0.5}
                    paddingX={2}
                  >
                    {isSearchItem && searchItem ? searchItem.collectionName : isProcess ? 'Process' : 'Collection'}
                  </Tag>
                </HStack>
              )}
              {renderActions()}
            </HStack>
            <Box flex={1} minW={0} onClick={onClick} cursor="pointer">
              {subtitle ? (
                <Text color="gray.600" fontSize="14px">
                  {subtitle}
                </Text>
              ) : null}
            </Box>
          </VStack>
        </>
      </HStack>
    </VStack>
  )
}

export default memo(SearchResultCard)
